'use client';

import { createClient } from '@/lib/supabase/client';

export interface Article {
  id: string;
  titleMn: string;
  titleKr: string;
  contentMn: string;
  contentKr: string;
  category: string;
  status: 'draft' | 'published';
  languages: string[];
  views: number;
  imageUrl: string | null;
  videoUrl: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

export interface CreateArticleInput {
  titleMn: string;
  titleKr: string;
  contentMn: string;
  contentKr: string;
  category: string;
  status: 'draft' | 'published';
  languages?: string[];
  imageUrl?: string | null;
  videoUrl?: string | null;
}

function toArticle(row: any): Article {
  return {
    id: row.id,
    titleMn: row.title_mn,
    titleKr: row.title_kr,
    contentMn: row.content_mn,
    contentKr: row.content_kr,
    category: row.category,
    status: row.status,
    languages: row.languages || ['mn', 'kr'],
    views: row.views,
    imageUrl: row.image_url || null,
    videoUrl: row.video_url || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    publishedAt: row.published_at,
  };
}

export const articleService = {
  async getAll(): Promise<Article[]> {
    const res = await fetch('/api/articles', { credentials: 'include' });
    if (!res.ok) {
      console.error('Error fetching articles:', res.statusText);
      return [];
    }
    const { data } = await res.json();
    return data?.map(toArticle) || [];
  },

  async getPublished(limit?: number): Promise<Article[]> {
    const supabase = createClient();
    let query = supabase
      .from('articles')
      .select('*')
      .eq('status', 'published')
      .order('published_at', { ascending: false });

    if (limit) {
      query = query.limit(limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching published articles:', error.message);
      return [];
    }
    return data?.map(toArticle) || [];
  },

  async create(input: CreateArticleInput): Promise<Article | null> {
    const res = await fetch('/api/articles', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    if (!res.ok) {
      const { error } = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error || 'Failed to create article');
    }

    const { data } = await res.json();
    return toArticle(data);
  },

  async delete(id: string): Promise<boolean> {
    const res = await fetch(`/api/articles/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!res.ok) {
      console.error('Error deleting article:', res.statusText);
      return false;
    }
    return true;
  },

  async update(id: string, input: Partial<CreateArticleInput>): Promise<Article | null> {
    const res = await fetch(`/api/articles/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });

    if (!res.ok) {
      const { error } = await res.json().catch(() => ({ error: res.statusText }));
      throw new Error(error || 'Failed to update article');
    }

    const { data } = await res.json();
    return toArticle(data);
  },

  async togglePublish(id: string, currentStatus: 'draft' | 'published'): Promise<Article | null> {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    return articleService.update(id, { status: newStatus });
  },

  async incrementViews(id: string): Promise<void> {
    const supabase = createClient();
    await supabase.rpc('increment_article_views', { article_id: id });
  },

  async getByCategory(category: string): Promise<Article[]> {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('articles')
      .select('*')
      .eq('status', 'published')
      .ilike('category', `%${category}%`)
      .order('published_at', { ascending: false });

    if (error) {
      console.error('Error fetching articles by category:', error.message);
      return [];
    }
    return data?.map(toArticle) || [];
  },

  async getStats(): Promise<{ total: number; published: number; draft: number; views: number }> {
    const res = await fetch('/api/articles', { credentials: 'include' });
    if (!res.ok) return { total: 0, published: 0, draft: 0, views: 0 };

    const { data } = await res.json();
    if (!data) return { total: 0, published: 0, draft: 0, views: 0 };

    const total = data.length;
    const published = data.filter((a: any) => a.status === 'published').length;
    const draft = data.filter((a: any) => a.status === 'draft').length;
    const views = data.reduce((sum: number, a: any) => sum + (a.views || 0), 0);

    return { total, published, draft, views };
  },
};
